/**
 * Still-image decoding with a fallback for HEIC/HEIF (the iPhone photo format), which Chrome and
 * Firefox can't decode. HEIF files are decoded with libheif (WebAssembly, LGPL), which is loaded on
 * first use only, in a worker. Safari decodes HEIC natively and never needs it.
 */
import type { HeifRequest, HeifResponse } from './heif-worker.ts';

/** ISO-BMFF `ftyp` brands of HEIF still images (HEVC-coded or generic). */
const HEIF_BRANDS = new Set(['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'mif1', 'msf1']);

export function isHeif(bytes: Uint8Array): boolean {
  if (bytes.length < 12 || ascii(bytes, 4, 4) !== 'ftyp') return false;
  const boxSize = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0);
  const end = Math.min(boxSize, bytes.length);
  // Major brand at 8, minor version at 12, compatible brands from 16.
  for (let pos = 8; pos + 4 <= end; pos += pos === 8 ? 8 : 4) {
    if (HEIF_BRANDS.has(ascii(bytes, pos, 4))) return true;
  }
  return false;
}

/**
 * Like `createImageBitmap(blob, options)`, but also decodes HEIF (in a worker). libheif applies the
 * file's rotation and mirroring itself, so `imageOrientation` only matters for other formats.
 */
export async function decodeImage(blob: Blob, options?: ImageBitmapOptions): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(blob, options);
  } catch (err) {
    if (!isHeif(new Uint8Array(await blob.slice(0, 64).arrayBuffer()))) throw err;
    return decodeHeif(blob, options);
  }
}

let worker: Worker | null = null;
let nextId = 0;
const pending = new Map<number, { resolve(bitmap: ImageBitmap): void; reject(err: Error): void }>();

function decodeHeif(blob: Blob, options?: ImageBitmapOptions): Promise<ImageBitmap> {
  if (!worker) {
    worker = new Worker(new URL('./heif-worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = ({ data }: MessageEvent<HeifResponse>) => {
      const request = pending.get(data.id);
      pending.delete(data.id);
      if ('bitmap' in data) request?.resolve(data.bitmap);
      else request?.reject(new Error(data.error));
    };
    worker.onerror = (event) => {
      // The worker itself failed (e.g. couldn't load): fail everything in flight and start afresh.
      for (const request of pending.values())
        request.reject(new Error(event.message || 'HEIF decoder failed'));
      pending.clear();
      worker?.terminate();
      worker = null;
    };
  }
  const id = nextId++;
  const request: HeifRequest = { id, blob, options };
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    worker!.postMessage(request);
  });
}

function ascii(bytes: Uint8Array, start: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(start, start + length));
}
